import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Whether the signed-in user has ever listed a sequence, has a Stripe connected account, or has
 * any sales. Three one-row reads (RLS: the user's own rows), cached for ten minutes, and only
 * run when `enabled` (a show-only owner, who is the only one that needs the answer).
 * Returns null until known and when a read fails, so callers treat "unknown" as a seller and
 * never hide Sales & payouts by mistake.
 */
export function useIsSeller(enabled: boolean): boolean | null {
  const { user } = useAuth();
  const userId = user?.id;
  const query = useQuery({
    queryKey: ['isSeller', userId],
    enabled: enabled && Boolean(userId),
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: async (): Promise<boolean> => {
      const [sequences, account, sales] = await Promise.all([
        supabase.from('sequences').select('id').eq('user_id', userId as string).limit(1),
        supabase.from('stripe_accounts').select('stripe_account_id').eq('user_id', userId as string).maybeSingle(),
        supabase.from('stripe_transactions').select('id').eq('seller_id', userId as string).limit(1),
      ]);
      const error = sequences.error ?? account.error ?? sales.error;
      if (error) throw error;
      return (sequences.data?.length ?? 0) > 0 || Boolean(account.data) || (sales.data?.length ?? 0) > 0;
    },
  });
  return query.isSuccess ? query.data : null;
}
